// src/pages/Dashboard.js
import React, { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../services/firebase/config';
import { 
  doc, 
  getDoc, 
 
  setDoc,
 
  serverTimestamp,
 
} from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import SummaryCards from '../components/dashboard/SummaryCards';
import PortfolioChart from '../components/dashboard/PortfolioChart';
import PortfolioTable from '../components/dashboard/PortfolioTable';
import RebalanceAlerts from '../components/dashboard/RebalanceAlerts';
import { usePortfolioData } from '../hooks/usePortfolioData';
import { useSmartRebalance } from '../hooks/useSmartRebalance';
import UpdatePricesButton from '../components/dashboard/UpdatePricesButton';
import { usePortfolio } from '../contexts/PortfolioContext';
import { moveAsset } from '../services/firebase/portfolioService';
import { Download } from 'lucide-react';

const Dashboard = () => {
  const [user] = useAuthState(auth);
  const { selectedPortfolio } = usePortfolio();
  const { 
    portfolioData, 
    isLoading: portfolioLoading, 
    error: portfolioError
  } = usePortfolioData(user?.email);
  
  const [currentPath, setCurrentPath] = useState('');
  const [rebalanceSettings, setRebalanceSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState(null);

  const handleExportPortfolio = () => {
    if (!portfolioData) {
      toast.error('Dışa aktarılacak portföy verisi bulunamadı');
      return;
    }
    
    try {
      const exportData = {
        exportedAt: new Date().toISOString(),
        portfolio: selectedPortfolio,
        data: portfolioData
      };

      const dataStr = JSON.stringify(exportData, null, 2);
      const blob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `portfolio-export-${selectedPortfolio?.name || 'data'}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Portföy dışa aktarıldı');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Dışa aktarma sırasında hata oluştu');
    }
  };
 

  // Rebalance ayarlarını yükle
  useEffect(() => {
    const loadSettings = async () => {
      if (!user?.email || !selectedPortfolio) return;

      setSettingsLoading(true);
      try {
        const portfolioDoc = doc(db, 'users', user.email, 'portfolios', selectedPortfolio.id);
        const settingsRef = doc(portfolioDoc, 'settings', 'rebalance');
        const settingsSnapshot = await getDoc(settingsRef);
        
        if (settingsSnapshot.exists()) {
          const settings = settingsSnapshot.data();
          setRebalanceSettings(settings);
          setSettingsError(null);
        } else {
          setRebalanceSettings({
            threshold: 5,
            targetBudget: 1000000,
            smartRules: {
              preventConflictingActions: true,
              minTransactionAmount: 100,
              maxTransactionPercentage: 10
            }
          });
        }
      } catch (error) {
        console.error('Ayar yükleme hatası:', error);
        setSettingsError(error);
        toast.error('Ayarlar yüklenirken bir hata oluştu');
      } finally {
        setSettingsLoading(false);
      }
    };

    loadSettings();
  }, [user, selectedPortfolio]);

  
// Dashboard.js içindeki handleUpdateAsset fonksiyonu

const handleUpdateAsset = async (symbol, updateData) => {
  if (!user?.email || !selectedPortfolio) {
    toast.error('Lütfen giriş yapın ve bir portföy seçin');
    return;
  }

  const toastId = toast.loading('Güncelleniyor...');
  
  try {
    const portfolioDoc = doc(db, 'users', user.email, 'portfolios', selectedPortfolio.id);
    const positionRef = doc(portfolioDoc, 'positions', symbol);

    await setDoc(positionRef, {
      ...updateData,
      updatedAt: serverTimestamp(),
      lastUpdated: serverTimestamp() // Eski veri yapısı için compat
    }, { merge: true });

    toast.success('Varlık güncellendi', { id: toastId });
  } catch (error) {
    console.error('Varlık güncelleme hatası:', error);
    toast.error('Güncelleme başarısız oldu', { id: toastId });
    throw error;
  }
};

const handleMoveAsset = async (symbol, targetCat, targetSubCat) => {
    if (!selectedPortfolio?.id || !user?.email) return;
    try {
        await moveAsset(user.email, selectedPortfolio.id, symbol, targetCat, targetSubCat);
        toast.success("Varlık başarıyla taşındı.");
    } catch (e) {
        toast.error("Taşıma işlemi başarısız: " + e.message);
    }
};

  // Kategori seçimini yönet
  const handleSelectCategory = (category) => {
    const pathParts = category.category.split(' > ');
    const newPath = pathParts.join('/');
    setCurrentPath(newPath);
  };

  // Rebalance uyarılarını al
  const alerts = useSmartRebalance(
    (!portfolioLoading && portfolioData) ? portfolioData : null,
    (!settingsLoading && rebalanceSettings) ? rebalanceSettings : null,
    user?.email
  );

  // Yükleme durumunu kontrol et
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-medium">Lütfen giriş yapın</h2>
        </div>
      </div>
    );
  }

  if (portfolioLoading || settingsLoading ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <div className="text-sm text-gray-500">Yükleniyor...</div>
        </div>
      </div>
    );
  }

  if (portfolioError || settingsError) {
    return (
      <div className="max-w-7xl mx-auto p-4">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          {portfolioError && <div>Portföy verisi hatası: {portfolioError.message}</div>}
          {settingsError && <div>Ayarlar hatası: {settingsError.message}</div>}
        </div>
      </div>
    );
  }

  const currentData = (() => {
    if (!portfolioData?.portfolioItems) return [];
    if (!currentPath) return portfolioData.portfolioItems;

    const parts = currentPath.split('/').filter(Boolean);
    const category = portfolioData.portfolioItems.find(item => item.name === parts[0]);
    
    if (!category) return [];
    if (parts.length === 1) {
      return category.subCategories?.length > 0 
        ? category.subCategories 
        : category.items || [];
    }
    if (parts.length === 2) {
      const subCategory = category.subCategories?.find(sub => sub.name === parts[1]);
      return subCategory?.items || [];
    }
    return [];
  })();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0A0E23]">
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Header ve Güncelleme Butonu */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Portföy Yönetimi
          </h1>
          <div className="flex gap-2">
            <button
              onClick={handleExportPortfolio}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              title="Portföyü JSON olarak dışa aktar"
            >
              <Download size={20} />
              <span className="hidden sm:inline">Export JSON</span>
            </button>
            <UpdatePricesButton />
          </div>
        </div>

        {/* Özet Kartları */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <SummaryCards portfolioData={portfolioData} />
        </div>

        {/* Ana İçerik Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-[500px]">
          {/* Portföy Grafiği */}
          <div className="col-span-1 lg:col-span-4">
            <div className="bg-white dark:bg-[#151934] rounded-lg shadow-sm h-[500px]">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700/50">
                <h2 className="font-medium text-gray-900 dark:text-white">Portföy Dağılımı</h2>
              </div>
              <div className="p-4 h-[calc(600px-57px)]">
                <PortfolioChart 
                  data={currentData}
                  currentPath={currentPath}
                  onPathChange={setCurrentPath}
                />
              </div>
            </div>
          </div>

          {/* Portföy Tablosu */}
          <div className="col-span-1 lg:col-span-8">
            <div className="bg-white dark:bg-[#151934] rounded-lg shadow-sm h-[500px]">
              <PortfolioTable 
                data={currentData}
                currentPath={currentPath}
                onPathChange={setCurrentPath}
                targetPortfolio={portfolioData.targetPortfolio}
                onUpdateAsset={handleUpdateAsset}
                allCategories={portfolioData?.portfolioItems || []}
                onMoveAsset={handleMoveAsset}
              />
            </div>
          </div>
        </div>

        {/* Rebalance Uyarıları */}
        {Array.isArray(alerts) && alerts.length > 0 && (
          <div className="mt-6">
                <RebalanceAlerts 
              alerts={alerts}
              onSelectCategory={handleSelectCategory}
              userEmail={user?.email}
              portfolioId={selectedPortfolio?.id}
            />
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;